import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { 
  Card, CardBody, CardHeader, Button, ButtonGroup, 
  Row, Col, Container, Spinner, Form, FormGroup, Label, Input
} from 'reactstrap';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  ArcElement,
  Title, 
  Tooltip, 
  Legend, 
  Filler
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, parseISO, formatISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { saleActions } from '../../sagas/saleSlice';
import { getTotalSalesForDateRange } from '../../services/saleService';
import { FaChartLine, FaChartBar, FaChartPie, FaCalendarAlt, FaDownload } from 'react-icons/fa';
import { formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const VentesStatistiques = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // State
  const [dateRange, setDateRange] = useState('month');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [salesData, setSalesData] = useState(null);

  // Selectors from redux store
  const {
    totalVentes,
    totalVentesDirect,
    totalPOSSales,
    totalSalesReturns,
    invoicePayed,
    invoiceNotPayed,
    invoicePayedPartially
  } = useSelector((state) => state.sales);

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: t('statistics.salesOverTime'),
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: t('statistics.salesDistribution'),
      },
    },
  };

  // Effect for fetching data
  useEffect(() => {
    setIsLoading(true);
    
    // Calculate date range
    let startDate, endDate;
    switch (dateRange) {
      case 'week':
        startDate = formatISO(startOfWeek(new Date(), { weekStartsOn: 1 }), { representation: 'date' });
        endDate = formatISO(endOfWeek(new Date(), { weekStartsOn: 1 }), { representation: 'date' });
        break;
      case 'month':
        startDate = formatISO(startOfMonth(new Date()), { representation: 'date' });
        endDate = formatISO(endOfMonth(new Date()), { representation: 'date' });
        break;
      case 'year':
        startDate = formatISO(startOfYear(new Date()), { representation: 'date' });
        endDate = formatISO(endOfYear(new Date()), { representation: 'date' });
        break;
      case 'custom':
        startDate = customDateRange.startDate;
        endDate = customDateRange.endDate;
        break;
      default:
        startDate = formatISO(subDays(new Date(), 30), { representation: 'date' });
        endDate = formatISO(new Date(), { representation: 'date' });
    }

    // Fetch sales data
    dispatch(saleActions.getSalesData({ dateRange }));
    dispatch(saleActions.getSalesReport({ startDate, endDate }));
    
    // Fetch detailed sales data by date range for charts
    const fetchSalesData = async () => {
      try {
        const response = await getTotalSalesForDateRange(startDate, endDate);
        setSalesData(response.data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching sales data:', error);
        setIsLoading(false);
      }
    };
    
    fetchSalesData();
  }, [dateRange, customDateRange, dispatch]);

  // Handle date range changes
  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  const handleCustomDateChange = (e) => {
    const { name, value } = e.target;
    setCustomDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  // Handle PDF export
  const handleExportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Add header with date
    doc.setFillColor(52, 152, 219);
    doc.rect(0, 0, pageWidth, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text(t('statistics.salesReport'), pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.text(format(new Date(), 'dd/MM/yyyy HH:mm', { locale: fr }), pageWidth - 14, 8, { align: 'right' });
    
    // Add date range
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    let dateRangeText;
    switch (dateRange) {
      case 'week':
        dateRangeText = t('statistics.thisWeek');
        break;
      case 'month':
        dateRangeText = t('statistics.thisMonth');
        break;
      case 'year':
        dateRangeText = t('statistics.thisYear');
        break;
      case 'custom':
        dateRangeText = `${format(parseISO(customDateRange.startDate), 'dd/MM/yyyy', { locale: fr })} - ${format(parseISO(customDateRange.endDate), 'dd/MM/yyyy', { locale: fr })}`;
        break;
      default:
        dateRangeText = t('statistics.thisMonth');
    }
    doc.text(`${t('statistics.period')}: ${dateRangeText}`, 14, 40);
    
    // Add summary
    doc.setFontSize(16);
    doc.setTextColor(52, 152, 219);
    doc.text(t('statistics.summary'), 14, 55);
    doc.setDrawColor(52, 152, 219);
    doc.line(14, 57, 60, 57);
    
    // Summary table
    doc.autoTable({
      startY: 60,
      headStyles: { fillColor: [52, 152, 219], textColor: [255, 255, 255] },
      head: [[t('statistics.metric'), t('statistics.value')]],
      body: [
        [t('statistics.totalSales'), `${formatPrice(totalVentes || 0)} DH`],
        [t('statistics.directSales'), `${formatPrice(totalVentesDirect || 0)} DH`],
        [t('statistics.posSales'), `${formatPrice(totalPOSSales || 0)} DH`],
        [t('statistics.salesReturns'), `${formatPrice(totalSalesReturns || 0)} DH`],
      ],
    });
    
    // Payment Status table
    doc.setFontSize(16);
    doc.setTextColor(52, 152, 219);
    doc.text(t('statistics.paymentStatus'), 14, doc.previousAutoTable.finalY + 20);
    doc.setDrawColor(52, 152, 219);
    doc.line(14, doc.previousAutoTable.finalY + 22, 80, doc.previousAutoTable.finalY + 22);
    
    doc.autoTable({
      startY: doc.previousAutoTable.finalY + 25,
      headStyles: { fillColor: [52, 152, 219], textColor: [255, 255, 255] },
      head: [[t('statistics.status'), t('statistics.amount')]],
      body: [
        [t('statistics.paid'), `${formatPrice(invoicePayed || 0)} DH`],
        [t('statistics.unpaid'), `${formatPrice(invoiceNotPayed || 0)} DH`],
        [t('statistics.partiallyPaid'), `${formatPrice(invoicePayedPartially || 0)} DH`],
      ],
    });
    
    // Add footer
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `${t('statistics.generatedOn')}: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: fr })}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
      doc.text(
        `${i} / ${totalPages}`,
        pageWidth - 10,
        pageHeight - 10,
        { align: 'right' }
      );
    }
    
    // Add top products if available
    if (salesData && salesData.topProducts && salesData.topProducts.length > 0) {
      doc.addPage();
      
      // Add header
      doc.setFillColor(52, 152, 219);
      doc.rect(0, 0, pageWidth, 30, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.text(t('statistics.topSellingProducts'), pageWidth / 2, 15, { align: 'center' });
      
      // Top products table
      const topProductsBody = salesData.topProducts.map(product => [
        product.name,
        product.quantity,
        `${formatPrice(product.revenue)} DH`
      ]);
      
      doc.autoTable({
        startY: 40,
        headStyles: { fillColor: [52, 152, 219], textColor: [255, 255, 255] },
        head: [[t('statistics.product'), t('statistics.quantity'), t('statistics.revenue')]],
        body: topProductsBody,
      });
    }
    
    doc.save(`rapport_ventes_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  // Prepare chart data
  const salesChartData = useMemo(() => {
    if (!salesData || !salesData.dailySales) return null;
    
    const labels = salesData.dailySales.map(item => format(parseISO(item.date), 'dd/MM'));
    const amounts = salesData.dailySales.map(item => item.amount || 0);
    
    return {
      labels,
      datasets: [
        {
          label: t('statistics.salesAmount'),
          data: amounts,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.4,
          fill: true
        }
      ]
    };
  }, [salesData, t]);

  // Distribution data for pie chart
  const distributionData = useMemo(() => {
    if (!totalVentes && !totalVentesDirect && !totalPOSSales) return null;
    
    return {
      labels: [
        t('statistics.invoiceSales'), 
        t('statistics.directSales'), 
        t('statistics.posSales')
      ],
      datasets: [
        {
          data: [
            totalVentes - totalVentesDirect - totalPOSSales, 
            totalVentesDirect, 
            totalPOSSales
          ],
          backgroundColor: [
            'rgba(54, 162, 235, 0.8)',
            'rgba(255, 206, 86, 0.8)',
            'rgba(75, 192, 192, 0.8)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)'
          ],
          borderWidth: 1,
        },
      ],
    };
  }, [totalVentes, totalVentesDirect, totalPOSSales, t]);

  // Payment status data for doughnut chart
  const paymentStatusData = useMemo(() => {
    return {
      labels: [
        t('statistics.paid'), 
        t('statistics.unpaid'), 
        t('statistics.partiallyPaid')
      ],
      datasets: [
        {
          data: [invoicePayed || 0, invoiceNotPayed || 0, invoicePayedPartially || 0],
          backgroundColor: [
            'rgba(75, 192, 192, 0.8)',
            'rgba(255, 99, 132, 0.8)',
            'rgba(255, 159, 64, 0.8)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(255, 159, 64, 1)'
          ],
          borderWidth: 1,
        },
      ],
    };
  }, [invoicePayed, invoiceNotPayed, invoicePayedPartially, t]);

  // Sales vs returns data for bar chart
  const salesVsReturnsData = {
    labels: [t('statistics.sales'), t('statistics.returns')],
    datasets: [
      {
        label: t('statistics.amount'),
        data: [totalVentes || 0, totalSalesReturns || 0],
        backgroundColor: [
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 99, 132, 0.8)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 1
      }
    ]
  };

  // Render summary cards
  const renderSummaryCards = () => {
    return (
      <Row className="mb-4">
        <Col lg="3" md="6">
          <Card className="shadow-sm mb-3 h-100">
            <CardBody>
              <div className="d-flex justify-content-between">
                <div>
                  <p className="text-muted text-uppercase fw-semibold small mb-2">
                    {t('statistics.totalSales')}
                  </p>
                  <h3 className="mb-0">{formatPrice(totalVentes || 0)} DH</h3>
                </div>
                <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                  <FaChartLine className="text-primary" size={24} />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        
        <Col lg="3" md="6">
          <Card className="shadow-sm mb-3 h-100">
            <CardBody>
              <div className="d-flex justify-content-between">
                <div>
                  <p className="text-muted text-uppercase fw-semibold small mb-2">
                    {t('statistics.directSales')}
                  </p>
                  <h3 className="mb-0">{formatPrice(totalVentesDirect || 0)} DH</h3>
                </div>
                <div className="rounded-circle bg-success bg-opacity-10 p-3">
                  <FaChartBar className="text-success" size={24} />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        
        <Col lg="3" md="6">
          <Card className="shadow-sm mb-3 h-100">
            <CardBody>
              <div className="d-flex justify-content-between">
                <div>
                  <p className="text-muted text-uppercase fw-semibold small mb-2">
                    {t('statistics.posSales')}
                  </p>
                  <h3 className="mb-0">{formatPrice(totalPOSSales || 0)} DH</h3>
                </div>
                <div className="rounded-circle bg-info bg-opacity-10 p-3">
                  <FaChartPie className="text-info" size={24} />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        
        <Col lg="3" md="6">
          <Card className="shadow-sm mb-3 h-100">
            <CardBody>
              <div className="d-flex justify-content-between">
                <div>
                  <p className="text-muted text-uppercase fw-semibold small mb-2">
                    {t('statistics.salesReturns')}
                  </p>
                  <h3 className="mb-0">{formatPrice(totalSalesReturns || 0)} DH</h3>
                </div>
                <div className="rounded-circle bg-warning bg-opacity-10 p-3">
                  <FaCalendarAlt className="text-warning" size={24} />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  };

  // Render loading spinner when data is being fetched
  if (isLoading) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2">{t('common.loading')}</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h2 className="mb-4">
        <FaChartLine className="me-2 text-primary" />
        {t('statistics.salesStatistics')}
      </h2>
      
      {/* Date range selector */}
      <Card className="shadow-sm mb-4">
        <CardBody>
          <div className="d-flex justify-content-between flex-wrap align-items-center">
            <ButtonGroup className="mb-2 mb-md-0">
              <Button 
                color={dateRange === 'week' ? 'primary' : 'outline-primary'} 
                onClick={() => handleDateRangeChange('week')}
              >
                {t('statistics.thisWeek')}
              </Button>
              <Button 
                color={dateRange === 'month' ? 'primary' : 'outline-primary'} 
                onClick={() => handleDateRangeChange('month')}
              >
                {t('statistics.thisMonth')}
              </Button>
              <Button 
                color={dateRange === 'year' ? 'primary' : 'outline-primary'} 
                onClick={() => handleDateRangeChange('year')}
              >
                {t('statistics.thisYear')}
              </Button>
              <Button 
                color={dateRange === 'custom' ? 'primary' : 'outline-primary'} 
                onClick={() => handleDateRangeChange('custom')}
              >
                {t('statistics.custom')}
              </Button>
            </ButtonGroup>
            
            <Button color="success" onClick={handleExportPDF}>
              <FaDownload className="me-2" />
              {t('common.exportPDF')}
            </Button>
          </div>
            {dateRange === 'custom' && (
            <Row className="mt-3">
              <Col md="5">
                <FormGroup>
                  <Label for="startDate">{t('statistics.startDate')}</Label>
                  <Input 
                    type="date" 
                    id="startDate"
                    name="startDate"
                    value={customDateRange.startDate}
                    onChange={handleCustomDateChange}
                    max={customDateRange.endDate}
                  />
                </FormGroup>
              </Col>
              <Col md="5">
                <FormGroup>
                  <Label for="endDate">{t('statistics.endDate')}</Label>
                  <Input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={customDateRange.endDate}
                    onChange={handleCustomDateChange}
                    min={customDateRange.startDate}
                  />
                </FormGroup>
              </Col>
              <Col md="2" className="d-flex align-items-end">
                <Button 
                  color="primary" 
                  className="w-100"
                  onClick={() => {
                    setDateRange('custom');
                  }}
                  disabled={!customDateRange.startDate || !customDateRange.endDate}
                >
                  {t('common.apply')}
                </Button>
              </Col>
            </Row>
          )}
          
          {/* Quick date presets */}
          {dateRange === 'custom' && (
            <Row className="mt-2">
              <Col>
                <div className="d-flex flex-wrap gap-2 justify-content-start">
                  <Button 
                    color="outline-secondary" 
                    size="sm" 
                    onClick={() => setCustomDateRange({
                      startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
                      endDate: format(new Date(), 'yyyy-MM-dd')
                    })}
                  >
                    {t('common.last7Days')}
                  </Button>
                  <Button 
                    color="outline-secondary" 
                    size="sm" 
                    onClick={() => setCustomDateRange({
                      startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
                      endDate: format(new Date(), 'yyyy-MM-dd')
                    })}
                  >
                    {t('common.last30Days')}
                  </Button>
                  <Button 
                    color="outline-secondary" 
                    size="sm" 
                    onClick={() => setCustomDateRange({
                      startDate: format(startOfMonth(subDays(new Date(), 30)), 'yyyy-MM-dd'),
                      endDate: format(endOfMonth(subDays(new Date(), 30)), 'yyyy-MM-dd')
                    })}
                  >
                    {t('common.lastMonth')}
                  </Button>
                  <Button 
                    color="outline-secondary" 
                    size="sm" 
                    onClick={() => setCustomDateRange({
                      startDate: format(startOfYear(new Date()), 'yyyy-MM-dd'),
                      endDate: format(new Date(), 'yyyy-MM-dd')
                    })}
                  >
                    {t('common.ytd')}
                  </Button>
                </div>
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>
        {/* Summary Cards */}
      {renderSummaryCards()}
      
      {/* Chart Tabs */}
      <Card className="shadow-sm mb-4">
        <CardHeader className="bg-white">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <ButtonGroup className="mb-2 mb-md-0 flex-wrap" style={{ maxWidth: '100%', overflow: 'auto' }}>
              <Button 
                color={activeTab === 'overview' ? 'primary' : 'outline-primary'} 
                onClick={() => handleTabChange('overview')}
                className="text-nowrap"
              >
                {t('statistics.overview')}
              </Button>
              <Button 
                color={activeTab === 'distribution' ? 'primary' : 'outline-primary'} 
                onClick={() => handleTabChange('distribution')}
                className="text-nowrap"
              >
                {t('statistics.distribution')}
              </Button>
              <Button 
                color={activeTab === 'payment' ? 'primary' : 'outline-primary'} 
                onClick={() => handleTabChange('payment')}
                className="text-nowrap"
              >
                {t('statistics.paymentStatus')}
              </Button>
              <Button 
                color={activeTab === 'comparison' ? 'primary' : 'outline-primary'} 
                onClick={() => handleTabChange('comparison')}
                className="text-nowrap"
              >
                {t('statistics.salesVsReturns')}
              </Button>
            </ButtonGroup>
          </div>
        </CardHeader>
        <CardBody>          <div style={{ height: '400px' }}>
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center h-100">
                <Spinner color="primary" />
                <p className="ms-3 mb-0">{t('common.loading')}</p>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && salesChartData ? (
                  <Line options={chartOptions} data={salesChartData} />
                ) : activeTab === 'overview' && !salesChartData && (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <p className="text-muted">{t('statistics.noData')}</p>
                  </div>
                )}
                
                {activeTab === 'distribution' && distributionData ? (
                  <Pie options={pieChartOptions} data={distributionData} />
                ) : activeTab === 'distribution' && !distributionData && (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <p className="text-muted">{t('statistics.noData')}</p>
                  </div>
                )}
                
                {activeTab === 'payment' && paymentStatusData && (
                  <Doughnut options={pieChartOptions} data={paymentStatusData} />
                )}
                
                {activeTab === 'comparison' && salesVsReturnsData && (
                  <Bar 
                    options={{
                      ...chartOptions, 
                      plugins: {
                        ...chartOptions.plugins,
                        title: {
                          display: true,
                          text: t('statistics.salesVsReturns')
                        }
                      }
                    }} 
                    data={salesVsReturnsData} 
                  />
                )}
              </>
            )}
          </div>
        </CardBody>
      </Card>
      
      {/* Detailed stats */}
      <Row>
        <Col md="6">
          <Card className="shadow-sm mb-4">
            <CardHeader className="bg-white">
              <h5 className="mb-0">{t('statistics.invoiceStatus')}</h5>
            </CardHeader>
            <CardBody>
              <table className="table table-sm">
                <tbody>
                  <tr>
                    <td className="text-muted">{t('statistics.invoicesPaid')}</td>
                    <td className="text-end fw-bold text-success">
                      {formatPrice(invoicePayed || 0)} DH
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">{t('statistics.invoicesUnpaid')}</td>
                    <td className="text-end fw-bold text-danger">
                      {formatPrice(invoiceNotPayed || 0)} DH
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">{t('statistics.invoicesPartiallyPaid')}</td>
                    <td className="text-end fw-bold text-warning">
                      {formatPrice(invoicePayedPartially || 0)} DH
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardBody>
          </Card>
        </Col>
        
        <Col md="6">
          <Card className="shadow-sm mb-4">
            <CardHeader className="bg-white">
              <h5 className="mb-0">{t('statistics.salesBreakdown')}</h5>
            </CardHeader>
            <CardBody>
              <table className="table table-sm">
                <tbody>
                  <tr>
                    <td className="text-muted">{t('statistics.invoiceSales')}</td>
                    <td className="text-end fw-bold">
                      {formatPrice((totalVentes || 0) - (totalVentesDirect || 0) - (totalPOSSales || 0))} DH
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">{t('statistics.directSales')}</td>
                    <td className="text-end fw-bold">
                      {formatPrice(totalVentesDirect || 0)} DH
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">{t('statistics.posSales')}</td>
                    <td className="text-end fw-bold">
                      {formatPrice(totalPOSSales || 0)} DH
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">{t('statistics.totalSales')}</td>
                    <td className="text-end fw-bold text-primary">
                      {formatPrice(totalVentes || 0)} DH
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardBody>
          </Card>
        </Col>
      </Row>
      
      {/* Conditional render for additional insights */}
      {salesData && salesData.topProducts && (
        <Card className="shadow-sm mb-4">
          <CardHeader className="bg-white">
            <h5 className="mb-0">{t('statistics.topSellingProducts')}</h5>
          </CardHeader>
          <CardBody>
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>{t('statistics.product')}</th>
                  <th className="text-center">{t('statistics.quantity')}</th>
                  <th className="text-end">{t('statistics.revenue')}</th>
                </tr>
              </thead>
              <tbody>
                {salesData.topProducts.map((product, index) => (
                  <tr key={index}>
                    <td>{product.name}</td>
                    <td className="text-center">{product.quantity}</td>
                    <td className="text-end">{formatPrice(product.revenue)} DH</td>
                  </tr>
                ))}
                {!salesData.topProducts.length && (
                  <tr>
                    <td colSpan="3" className="text-center text-muted py-4">
                      {t('statistics.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}
    </Container>
  );
};

export default VentesStatistiques;